/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

interface Props extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Change the spacing between the items */
  spacing?: 'sm' | 'md' | 'lg' | 'xl' | 'none';
  /** Display it inline with another component */
  inline?: boolean;
  /** How many columns should this grid have */
  columns?: number;
  /** Pass in the test id */
  testSelector?: string;
  /** Stop the grid items from expanding the full width */
  fill?: boolean;
  /** To automatically set the column count, based on min/max width */
  autoFill?: boolean;
  /** For use with autoFill, set the min width of the items */
  minWidth?: string;
  /** For use with autoFill, set the max width of the items */
  maxWidth?: string;
  /** To make each column only as wide as the content it contains */
  fitContent?: boolean;
  /** Set this grid disabled */
  disabled?: boolean;
}

const ODSGrid = forwardRef<HTMLDivElement, Props>(function ODSGrid(
  {
    className,
    columns = 4,
    children,
    spacing = 'md',
    inline = false,
    testSelector,
    fill = true,
    autoFill,
    minWidth = '0',
    maxWidth = '1fr',
    fitContent,
    disabled,
    ...componentProps
  },
  ref
) {
  const display = inline ? 'd-inline-grid' : 'd-grid';
  let gap = 'gap-0';

  switch (spacing) {
    case 'sm':
      gap = 'gap-1';
      break;
    case 'md':
      gap = 'gap-2';
      break;
    case 'lg':
      gap = 'gap-3';
      break;
    case 'xl':
      gap = 'gap-4';
      break;
    default:
  }

  const getGridTemplateColumns = (): string => {
    if (autoFill) {
      return `repeat(auto-fill, minmax(${minWidth}, ${maxWidth}))`;
    }
    if (fitContent) {
      return `repeat(${columns}, fit-content(100%))`;
    }
    return `repeat(${columns}, minmax(0, ${fill ? '1fr' : 'max-content'}))`;
  };

  return (
    <div
      className={cx(display, gap, className, { disabled })}
      css={{
        gridTemplateColumns: getGridTemplateColumns(),
        gridColumn: '1/-1',
      }}
      data-testid={testSelector}
      ref={ref}
      {...componentProps}
    >
      {children}
    </div>
  );
});

export default ODSGrid;
